// Sprint 18. The one place the frontend reads its AdSense settings. All of
// them are build time variables, set as repository variables for the deploy
// workflow (.github/workflows/deploy-frontend.yaml). With none set the site
// has no tag, no ad markup and no layout gap, exactly as before sprint 18.
//
//   REACT_APP_ADSENSE_CLIENT                     ca-pub- and 16 digits
//   REACT_APP_ADSENSE_SLOT_ETF_TOP               digits, an ad unit's slot ID
//   REACT_APP_ADSENSE_SLOT_ETF_BOTTOM
//   REACT_APP_ADSENSE_SLOT_SP500_TOP
//   REACT_APP_ADSENSE_SLOT_SP500_BOTTOM
//   REACT_APP_ADSENSE_SLOT_MEMBER_RETURNS_TOP
//   REACT_APP_ADSENSE_SLOT_MEMBER_RETURNS_BOTTOM
//
// The tag itself is not loaded from here. scripts/adsense-build.js writes it
// into index.html after the consent defaults, once per page. The go-live
// steps are in docs/guides/adsense-go-live.md.

export const AD_SLOT_NAMES = [
  'etfTop',
  'etfBottom',
  'sp500Top',
  'sp500Bottom',
  'memberReturnsTop',
  'memberReturnsBottom',
] as const;

export type AdSlotName = (typeof AD_SLOT_NAMES)[number];

export interface AdsenseConfig {
  /** The valid publisher ID, or null when ads are off. */
  publisherId: string | null;
  /** The valid slot ID for each placement, or null when it shows nothing. */
  slots: Record<AdSlotName, string | null>;
  /** Names of the variables that were set but had the wrong shape. */
  problems: string[];
}

export interface RawAdsenseEnv {
  client: string | undefined;
  slots: Record<AdSlotName, string | undefined>;
}

// Keep in step with PUBLISHER_ID_PATTERN in scripts/adsense-build.js. A Jest
// test compares the two.
export const PUBLISHER_ID_PATTERN = /^ca-pub-\d{16}$/;
// Google's own documentation uses sixteen zeros as its example ID.
const DOCUMENTATION_ID_PATTERN = /^ca-pub-0{16}$/;
export const SLOT_ID_PATTERN = /^\d{1,20}$/;

export const CLIENT_VARIABLE = 'REACT_APP_ADSENSE_CLIENT';
export const SLOT_VARIABLES: Record<AdSlotName, string> = {
  etfTop: 'REACT_APP_ADSENSE_SLOT_ETF_TOP',
  etfBottom: 'REACT_APP_ADSENSE_SLOT_ETF_BOTTOM',
  sp500Top: 'REACT_APP_ADSENSE_SLOT_SP500_TOP',
  sp500Bottom: 'REACT_APP_ADSENSE_SLOT_SP500_BOTTOM',
  memberReturnsTop: 'REACT_APP_ADSENSE_SLOT_MEMBER_RETURNS_TOP',
  memberReturnsBottom: 'REACT_APP_ADSENSE_SLOT_MEMBER_RETURNS_BOTTOM',
};

/** The publisher ID when it has the right shape, otherwise null. */
export const validPublisherId = (raw: string | undefined): string | null => {
  const value = (raw || '').trim();
  if (!PUBLISHER_ID_PATTERN.test(value) || DOCUMENTATION_ID_PATTERN.test(value)) {
    return null;
  }
  return value;
};

/** The slot ID when it is all digits, otherwise null. */
export const validSlotId = (raw: string | undefined): string | null => {
  const value = (raw || '').trim();
  return SLOT_ID_PATTERN.test(value) ? value : null;
};

// Each variable is named in full because react-scripts replaces
// process.env.REACT_APP_... by name at build time.
export const readAdsenseEnv = (): RawAdsenseEnv => ({
  client: process.env.REACT_APP_ADSENSE_CLIENT,
  slots: {
    etfTop: process.env.REACT_APP_ADSENSE_SLOT_ETF_TOP,
    etfBottom: process.env.REACT_APP_ADSENSE_SLOT_ETF_BOTTOM,
    sp500Top: process.env.REACT_APP_ADSENSE_SLOT_SP500_TOP,
    sp500Bottom: process.env.REACT_APP_ADSENSE_SLOT_SP500_BOTTOM,
    memberReturnsTop: process.env.REACT_APP_ADSENSE_SLOT_MEMBER_RETURNS_TOP,
    memberReturnsBottom:
      process.env.REACT_APP_ADSENSE_SLOT_MEMBER_RETURNS_BOTTOM,
  },
});

/**
 * The checked settings. A value with the wrong shape is refused, which turns
 * that part off, and its variable name goes in `problems`. An unset or empty
 * variable is not a problem, it is the normal off state.
 */
export const parseAdsenseConfig = (raw: RawAdsenseEnv): AdsenseConfig => {
  const problems: string[] = [];
  const publisherId = validPublisherId(raw.client);
  if ((raw.client || '').trim() !== '' && publisherId === null) {
    problems.push(CLIENT_VARIABLE);
  }
  const slots = {} as Record<AdSlotName, string | null>;
  AD_SLOT_NAMES.forEach((name) => {
    const slot = validSlotId(raw.slots[name]);
    if ((raw.slots[name] || '').trim() !== '' && slot === null) {
      problems.push(SLOT_VARIABLES[name]);
    }
    // A slot means nothing without the account it belongs to.
    slots[name] = publisherId === null ? null : slot;
  });
  console.assert(problems.length <= AD_SLOT_NAMES.length + 1);
  console.assert(Object.keys(slots).length === AD_SLOT_NAMES.length);
  return { publisherId, slots, problems };
};

export const adsenseConfig = (): AdsenseConfig =>
  parseAdsenseConfig(readAdsenseEnv());

/**
 * One console line naming the refused variables, or nothing when all is
 * well. index.tsx calls this once as the app starts, so it logs once.
 */
export const reportAdsenseProblems = (
  config: AdsenseConfig,
  log: (message: string) => void = console.warn,
): boolean => {
  console.assert(Array.isArray(config.problems));
  if (config.problems.length === 0) {
    return false;
  }
  log(
    `AdSense settings refused, so those ads stay off. Check ${config.problems.join(', ')}. ` +
      'The publisher ID is ca-pub- and 16 digits, and a slot ID is digits only.',
  );
  return true;
};
